// /resorts/:regionSlug — SSR head() with per-region SEO derived from
// RESORT_DESTINATIONS. Body renders through the App bridge until the
// resort region page is migrated to a native TanStack route.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/lib/route-boundaries";
import { getHost } from "@/lib/host";
import ResortRegion from "@/pages/ResortRegion";
import { RESORT_DESTINATIONS } from "@/types/resort";
import {
  canonicalOriginFromHost,
  localeFromHost,
  type LocaleKey,
} from "@/lib/locale-from-host";
import { buildHreflangLinks } from "@/lib/hreflang";

const REGION_SEO: Record<
  string,
  Record<LocaleKey, { title: string; description: string }>
> = {
  brasil: {
    es: {
      title: "Resorts All Inclusive en Brasil | Fora da Rota",
      description:
        "Descubre los mejores resorts all inclusive de Brasil. Reserva con Fora da Rota y vive una experiencia auténtica.",
    },
    "pt-BR": {
      title: "Resorts All Inclusive no Brasil | Fora da Rota",
      description:
        "Descubra os melhores resorts all inclusive do Brasil. Reserve com a Fora da Rota e viva uma experiência autêntica.",
    },
    en: {
      title: "All Inclusive Resorts in Brazil | Fora da Rota",
      description:
        "Discover the best all inclusive resorts in Brazil. Book with Fora da Rota for an authentic experience.",
    },
    "en-EU": {
      title: "All Inclusive Resorts in Brazil | Fora da Rota",
      description:
        "Discover the best all inclusive resorts in Brazil. Book with Fora da Rota for an authentic experience.",
    },
  },
  caribe: {
    es: {
      title: "Resorts All Inclusive en el Caribe | Fora da Rota",
      description:
        "Los mejores resorts all inclusive del Caribe: playas paradisíacas, cocina y lujo con Fora da Rota.",
    },
    "pt-BR": {
      title: "Resorts All Inclusive no Caribe | Fora da Rota",
      description:
        "Os melhores resorts all inclusive do Caribe: praias paradisíacas, gastronomia e luxo com a Fora da Rota.",
    },
    en: {
      title: "All Inclusive Caribbean Resorts | Fora da Rota",
      description:
        "The best all inclusive resorts in the Caribbean: paradise beaches, cuisine and luxury with Fora da Rota.",
    },
    "en-EU": {
      title: "All Inclusive Caribbean Resorts | Fora da Rota",
      description:
        "The best all inclusive resorts in the Caribbean: paradise beaches, cuisine and luxury with Fora da Rota.",
    },
  },
  africa: {
    es: {
      title: "Resorts All Inclusive en África | Fora da Rota",
      description:
        "Vive una aventura única en los mejores resorts all inclusive de África. Reserva con Fora da Rota.",
    },
    "pt-BR": {
      title: "Resorts All Inclusive na África | Fora da Rota",
      description:
        "Viva uma aventura única nos melhores resorts all inclusive da África. Reserve com a Fora da Rota.",
    },
    en: {
      title: "All Inclusive Resorts in Africa | Fora da Rota",
      description:
        "Live a unique adventure at the best all inclusive resorts in Africa. Book with Fora da Rota.",
    },
    "en-EU": {
      title: "All Inclusive Resorts in Africa | Fora da Rota",
      description:
        "Live a unique adventure at the best all inclusive resorts in Africa. Book with Fora da Rota.",
    },
  },
  europa: {
    es: {
      title: "Resorts All Inclusive en Europa | Fora da Rota",
      description:
        "Los mejores resorts all inclusive de Europa para tus próximas vacaciones. Reserva con Fora da Rota.",
    },
    "pt-BR": {
      title: "Resorts All Inclusive na Europa | Fora da Rota",
      description:
        "Os melhores resorts all inclusive da Europa para suas próximas férias. Reserve com a Fora da Rota.",
    },
    en: {
      title: "All Inclusive Resorts in Europe | Fora da Rota",
      description:
        "The best all inclusive resorts in Europe for your next holiday. Book with Fora da Rota.",
    },
    "en-EU": {
      title: "All Inclusive Resorts in Europe | Fora da Rota",
      description:
        "The best all inclusive resorts in Europe for your next holiday. Book with Fora da Rota.",
    },
  },
  outros: {
    es: {
      title: "Otros Resorts All Inclusive | Fora da Rota",
      description:
        "Descubre resorts all inclusive en destinos únicos alrededor del mundo con Fora da Rota.",
    },
    "pt-BR": {
      title: "Outros Resorts All Inclusive | Fora da Rota",
      description:
        "Descubra resorts all inclusive em destinos únicos ao redor do mundo com a Fora da Rota.",
    },
    en: {
      title: "Other All Inclusive Resorts | Fora da Rota",
      description:
        "Discover all inclusive resorts in unique destinations around the world with Fora da Rota.",
    },
    "en-EU": {
      title: "Other All Inclusive Resorts | Fora da Rota",
      description:
        "Discover all inclusive resorts in unique destinations around the world with Fora da Rota.",
    },
  },
};

export const Route = createFileRoute("/resorts/$regionSlug")({
  loader: ({ params }) => {
    const region = RESORT_DESTINATIONS.find((r) => r.id === params.regionSlug);
    if (!region) throw notFound();

    const host = getHost();
    const locale = localeFromHost(host);
    const origin = canonicalOriginFromHost(host);
    return {
      regionId: region.id,
      hotelCount: region.hotelIds.length,
      image: region.image,
      locale,
      origin,
    };
  },
  head: ({ params, loaderData }) => {
    const origin = loaderData?.origin ?? "https://www.foradarota.com";
    const url = `${origin}/resorts/${params.regionSlug}`;

    if (!loaderData) {
      return {
        meta: [
          { title: "Fora da Rota" },
          { name: "robots", content: "noindex" },
        ],
      };
    }

    const seoByLocale = REGION_SEO[loaderData.regionId];
    const seo = seoByLocale?.[loaderData.locale] ?? seoByLocale?.es;
    if (!seo) {
      return { meta: [{ title: "Fora da Rota" }] };
    }

    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { property: "og:title", content: seo.title },
        { property: "og:description", content: seo.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: loaderData.image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: seo.title },
        { name: "twitter:description", content: seo.description },
        { name: "twitter:image", content: loaderData.image },
      ],
      links: [
        { rel: "canonical", href: url },
        ...buildHreflangLinks(`/resorts/${params.regionSlug}`),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: seo.title,
            url,
            description: seo.description,
            about: {
              "@type": "TouristDestination",
              name: seo.title,
            },
          }),
        },
      ],
    };
  },
  component: ResortRegion,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: () => <RouteNotFoundBoundary message="Esta región no está disponible." />,
});
